import { userService } from '@/api/user/userApi';
import { ChangePasswordDialog } from '@/components/profile/ChangePasswordDialog';
import { handleCuitInput, isCuitValid } from '@/utils/cuit-utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import {
  FieldHelp,
  useAutoStartTour,
  useGuideStore,
} from '@/features/guided-help';
import { useAuth } from '@/hooks/useAuth';
import { useTournamentStore } from '@/store/useTournamentStore';
import { User } from '@/types/user.model';
import { handleGlobalError } from '@/utils/error-handler';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Calendar,
  CreditCard,
  KeyRound,
  LogOut,
  Mail,
  Phone,
  Save,
  User as UserIcon,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER',
}

const profileSchema = z.object({
  name: z
    .string()
    .min(1, 'El nombre completo es requerido')
    .max(100, 'El nombre es demasiado largo'),
  email: z
    .string()
    .min(1, 'El correo electrónico es requerido')
    .email('Ingresa un correo electrónico válido')
    .max(255, 'El correo electrónico es demasiado largo'),
  cellphone: z
    .string()
    .min(1, 'El teléfono es requerido')
    .max(20, 'El teléfono es demasiado largo'),
  birthdate: z.string().min(1, 'La fecha de nacimiento es requerida'),
  gender: z.string().min(1, 'El género es requerido'),
  id: z
    .string()
    .min(11, { message: 'El CUIT debe tener exactamente 11 dígitos' })
    .max(11, { message: 'El CUIT debe tener exactamente 11 dígitos' })
    .regex(/^\d{11}$/, { message: 'El CUIT debe contener solo números' })
    .refine((cuit) => isCuitValid(cuit), {
      message: 'El CUIT ingresado no es válido',
    }),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface ProfileProps {
  isFullScreen?: boolean;
  onProfileComplete?: () => void;
}

const Profile: React.FC<ProfileProps> = ({
  isFullScreen = false,
  onProfileComplete,
}) => {
  const { user, hasManagerRole, clearTokens, isLoadingUser } = useAuth();
  const { clearTournaments } = useTournamentStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      cellphone: '',
      birthdate: '',
      gender: '',
      id: '',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        email: user.email || '',
        cellphone: user.cellphone || '',
        birthdate: user.birthdate ? user.birthdate.split('T')[0] : '',
        gender: user.gender || '',
        id: user.id || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'No se pudo identificar al usuario.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData: Pick<
        User,
        | 'email'
        | 'name'
        | 'gender'
        | 'birthdate'
        | 'cellphone'
        | 'id'
        | 'username'
      > = {
        ...data,
        birthdate: new Date(data.birthdate).toISOString(),
        username: user.username,
      };

      await userService.updateUser(updateData);

      toast({
        title: 'Perfil actualizado',
        description:
          'Tu información de perfil se ha actualizado correctamente.',
        variant: 'default',
      });

      if (isFullScreen && onProfileComplete) {
        onProfileComplete();
      }
    } catch (error) {
      handleGlobalError(
        error,
        'Error al actualizar perfil',
        'No se pudo actualizar tu información de perfil. Por favor, intenta nuevamente.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearTokens();
    clearTournaments(); // Clear tournament data when logging out
  };

  useAutoStartTour('panel.profile');

  const guidesEnabled = useGuideStore((state) => state.enabled);
  const setGuidesEnabled = useGuideStore((state) => state.setEnabled);
  const resetSeenGuides = useGuideStore((state) => state.resetSeen);

  if (isLoadingUser) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">
            Cargando información del perfil...
          </p>
        </div>
      </div>
    );
  }

  const containerClass = isFullScreen
    ? 'min-h-screen bg-gray-50 flex items-center justify-center px-4'
    : 'container mx-auto max-w-2xl px-4 py-8';

  const cardClass = isFullScreen ? 'w-full max-w-2xl shadow-xl' : 'shadow-lg';

  return (
    <div className={containerClass}>
      <Card className={cardClass}>
        <CardHeader className="text-center">
          {user?.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt={user.name || 'Usuario'}
              className="mx-auto mb-4 h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <UserIcon size={32} className="text-blue-600" />
            </div>
          )}
          <CardTitle className="text-2xl font-bold text-gray-900">
            {isFullScreen ? 'Completar Perfil' : 'Mi Perfil'}
          </CardTitle>
          {isFullScreen && (
            <p className="mt-2 text-gray-600">
              Para continuar usando la aplicación, necesitas completar tu
              información de CUIT/CUIL.
            </p>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {isFullScreen && hasManagerRole && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <CreditCard
                  size={20}
                  className="mt-0.5 flex-shrink-0 text-amber-600"
                />
                <div>
                  <h4 className="mb-1 font-semibold text-amber-900">
                    CUIT/CUIL Requerido
                  </h4>
                  <p className="text-sm text-amber-800">
                    Como usuario Manager, necesitas proporcionar tu CUIT/CUIL
                    para acceder a todas las funciones de la aplicación.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            data-guide-id="panel.profile.data"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Name Field */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Nombre completo *
                </Label>
                <div className="relative mt-1">
                  <UserIcon
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder="Ingresa tu nombre completo"
                    className={`pl-10 ${errors.name ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div className="md:col-span-2">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Correo electrónico *
                </Label>
                <div className="relative mt-1">
                  <Mail
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    placeholder="ejemplo@correo.com"
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <Label
                  htmlFor="cellphone"
                  className="text-sm font-medium text-gray-700"
                >
                  Teléfono *
                </Label>
                <div className="relative mt-1">
                  <Phone
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="cellphone"
                    {...register('cellphone')}
                    placeholder="Número de teléfono"
                    className={`pl-10 ${errors.cellphone ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.cellphone && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.cellphone.message}
                  </p>
                )}
              </div>

              {/* Birthdate Field */}
              <div>
                <Label
                  htmlFor="birthdate"
                  className="text-sm font-medium text-gray-700"
                >
                  Fecha de nacimiento *
                </Label>
                <div className="relative mt-1">
                  <Calendar
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="birthdate"
                    type="date"
                    {...register('birthdate')}
                    className={`pl-10 ${errors.birthdate ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.birthdate && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.birthdate.message}
                  </p>
                )}
              </div>

              {/* Gender Field */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Género *
                </Label>
                <Controller
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        className={`mt-1 ${errors.gender ? 'border-red-500' : ''}`}
                      >
                        <SelectValue placeholder="Seleccionar género" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Gender.MALE}>Masculino</SelectItem>
                        <SelectItem value={Gender.FEMALE}>Femenino</SelectItem>
                        <SelectItem value={Gender.OTHER}>Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.gender.message}
                  </p>
                )}
              </div>

              {/* CUIT/CUIL Field */}
              <div>
                <Label
                  htmlFor="id"
                  className="text-sm font-medium text-gray-700"
                >
                  CUIT/CUIL *
                </Label>
                <div className="relative mt-1">
                  <CreditCard
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
                  />
                  <Input
                    id="id"
                    {...register('id')}
                    placeholder="XX-XXXXXXXX-X"
                    className={`pl-10 ${errors.id ? 'border-red-500' : ''}`}
                    maxLength={11}
                    onInput={handleCuitInput}
                  />
                </div>
                {errors.id && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.id.message}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              {isFullScreen && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLogout}
                  className="flex-1"
                >
                  <LogOut size={18} className="mr-2" />
                  Cerrar Sesión
                </Button>
              )}
              <Button
                type="submit"
                className="flex-1"
                disabled={
                  !isValid || isSubmitting || (!isDirty && !isFullScreen)
                }
              >
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {isFullScreen ? 'Completar Perfil' : 'Guardar Cambios'}
                  </>
                )}
              </Button>
            </div>
          </form>

          {!isFullScreen && (
            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-900">Contraseña</h4>
              <p className="mt-1 text-sm text-gray-600">
                Cambiá la contraseña con la que entrás al panel.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setIsChangePasswordOpen(true)}
              >
                <KeyRound size={16} className="mr-2" />
                Cambiar contraseña
              </Button>
            </div>
          )}

          {!isFullScreen && (
            <div
              className="rounded-lg border border-gray-200 p-4"
              data-guide-id="panel.profile.guides"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">
                      Guías para managers nuevos
                    </h4>
                    <FieldHelp topicId="panel.profile.guides" />
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    Mostrar el recorrido guiado la primera vez que entrás a cada
                    sección.
                  </p>
                </div>
                <Switch
                  checked={guidesEnabled}
                  onCheckedChange={setGuidesEnabled}
                  aria-label="Guías para managers nuevos"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={resetSeenGuides}
              >
                Volver a ver las guías
              </Button>
            </div>
          )}

          {!isFullScreen && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="flex items-start gap-3">
                <Users
                  size={20}
                  className="mt-0.5 flex-shrink-0 text-blue-600"
                />
                <div>
                  <h4 className="mb-1 font-semibold text-blue-900">
                    Información de Perfil
                  </h4>
                  <p className="text-sm text-blue-800">
                    Mantén tu información actualizada para mejorar tu
                    experiencia en la plataforma.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!isFullScreen && user && (
        <ChangePasswordDialog
          open={isChangePasswordOpen}
          onOpenChange={setIsChangePasswordOpen}
          email={user.email}
        />
      )}
    </div>
  );
};

export default Profile;
